import { IAnalysisUseCase } from './i-analysis-use-case'
import { IQueryRepository } from '../repositories/i-query-repository'
import { LayerEntity } from '../models/entities/layer-entity'
import { AnalysisResultEntity } from '../models/entities/analysis-result-entity'
import { ResponseValueObject } from '../models/value-objects/response-value-object'
import { StatisticDefinition } from '../models/entities/statistic-definition-entity'
import { StatisticTypeEnum } from '../models/enums/statistic-type-enum'
import { LayerStatisticEntity } from '../models/entities/layer-statistic-entity'

export class EcosystemFragmentationUseCase implements IAnalysisUseCase {
  readonly id = 'ecosystem-fragmentation'

  private static readonly CODE_FIELDS = ['id_dist', 'UBIGEO', 'CCDD', 'CÓDIGO', 'CODIGOU', 'ubigeo']

  private static readonly GROUP_STAT: StatisticDefinition[] = [{
    statisticType: StatisticTypeEnum.Count,
    onStatisticField: '1',
    outStatisticFieldName: 'count',
  }]

  constructor(private readonly queryRepo: IQueryRepository) {}

  private buildWhereClause(fieldQuery: string, departmentCode: string, departmentName: string): string {
    const isCodeField = EcosystemFragmentationUseCase.CODE_FIELDS.some(f => fieldQuery.toUpperCase().includes(f.toUpperCase()))
    if (isCodeField) {
      return `${fieldQuery} LIKE '${departmentCode}%'`
    }
    return `UPPER(${fieldQuery}) = UPPER('${departmentName}')`
  }

  async execute(layers: LayerEntity[], departmentCode?: string | null, departmentName?: string | null): Promise<ResponseValueObject<AnalysisResultEntity>> {
    const extentLayerIdx = layers.findIndex(c => !!(departmentCode && c.fieldQuery && departmentName))
    const validIdx = extentLayerIdx >= 0 ? extentLayerIdx : 0
    const extentLayer = layers[validIdx]
    const baseWhere = departmentCode && extentLayer.fieldQuery && departmentName
      ? this.buildWhereClause(extentLayer.fieldQuery, departmentCode, departmentName)
      : '1=1'
    const extentRes = await this.queryRepo.queryLayerExtent(extentLayer.url ?? '', baseWhere)
    const baseExtent = extentRes.data

    const results = await Promise.all(
      layers.map(async (c, i) => {
        const where = departmentCode && c.fieldQuery && departmentName
          ? this.buildWhereClause(c.fieldQuery, departmentCode, departmentName)
          : '1=1'

        const totalCount = (await this.queryRepo.queryLayerCount(c.url ?? '', where)).data ?? 0

        let categoryStats: LayerStatisticEntity[] | undefined
        if (c.categoryField) {
          const catRes = await this.queryRepo.queryLayerStatistics(c.url ?? '', where, EcosystemFragmentationUseCase.GROUP_STAT, [c.categoryField])
          categoryStats = catRes.data ?? undefined
        }

        let spatialCrossCount: number | undefined
        if (i !== validIdx && baseExtent) {
          spatialCrossCount = (await this.queryRepo.queryLayerCount(c.url ?? '', where, baseExtent)).data
        }

        return {
          layerName: c.name,
          layerUrl: c.url ?? '',
          featureCount: totalCount,
          categoryStats,
          spatialCrossCount: spatialCrossCount ?? totalCount,
        }
      })
    )

    return new ResponseValueObject(true, {
      analysisId: this.id,
      analysisName: 'Fragmentación de Ecosistemas Estratégicos',
      completedAt: new Date(),
      layerResults: results,
      totalFeatures: results.reduce((s, r) => s + r.featureCount, 0),
    })
  }
}
